import type { Rect } from '@/core/layout/geometry';
import type { Fill, Scene, SceneNode } from './scene';
import { qrPathData, topRoundedPath } from './svg';

/**
 * Отрисовка сцены на canvas.
 *
 * Это не скриншот DOM: рисуются те же примитивы, что уходят в SVG, из того же
 * расчёта макета. Текст уже превращён в контуры, поэтому подстановка шрифта
 * здесь невозможна в принципе.
 */

function applyFill(ctx: CanvasRenderingContext2D, fill: Fill, rect: Rect): string | CanvasGradient {
  if (fill.kind === 'solid') return fill.color;
  const gradient = ctx.createLinearGradient(rect.x, rect.y, rect.x + rect.w, rect.y + rect.h);
  gradient.addColorStop(0, fill.from);
  gradient.addColorStop(1, fill.to);
  return gradient;
}

function roundedRectPath(ctx: CanvasRenderingContext2D, rect: Rect, radius: number): void {
  const r = Math.max(0, Math.min(radius, rect.w / 2, rect.h / 2));
  ctx.beginPath();
  if (r === 0) {
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    return;
  }
  ctx.moveTo(rect.x + r, rect.y);
  ctx.lineTo(rect.x + rect.w - r, rect.y);
  ctx.quadraticCurveTo(rect.x + rect.w, rect.y, rect.x + rect.w, rect.y + r);
  ctx.lineTo(rect.x + rect.w, rect.y + rect.h - r);
  ctx.quadraticCurveTo(rect.x + rect.w, rect.y + rect.h, rect.x + rect.w - r, rect.y + rect.h);
  ctx.lineTo(rect.x + r, rect.y + rect.h);
  ctx.quadraticCurveTo(rect.x, rect.y + rect.h, rect.x, rect.y + rect.h - r);
  ctx.lineTo(rect.x, rect.y + r);
  ctx.quadraticCurveTo(rect.x, rect.y, rect.x + r, rect.y);
  ctx.closePath();
}

function drawNode(ctx: CanvasRenderingContext2D, node: SceneNode): void {
  switch (node.type) {
    case 'rect': {
      ctx.fillStyle = applyFill(ctx, node.fill, node.rect);
      roundedRectPath(ctx, node.rect, node.radius);
      ctx.fill();
      return;
    }
    case 'topbar': {
      ctx.fillStyle = applyFill(ctx, node.fill, node.rect);
      ctx.fill(new Path2D(topRoundedPath(node.rect, node.radius)));
      return;
    }
    case 'stroke': {
      ctx.strokeStyle = node.color;
      ctx.lineWidth = node.width;
      roundedRectPath(ctx, node.rect, node.radius);
      ctx.stroke();
      return;
    }
    case 'qr': {
      ctx.fillStyle = node.dark;
      const d = qrPathData(
        node.matrix,
        node.moduleSize,
        node.rect.x,
        node.rect.y,
        node.quietZoneModules,
      );
      if (d !== '') ctx.fill(new Path2D(d));
      return;
    }
    case 'path': {
      ctx.fillStyle = node.fill;
      ctx.fill(new Path2D(node.d));
      return;
    }
  }
}

export type CanvasOptions = {
  /** Непрозрачная подложка: обязательна для JPEG. */
  opaqueBackground?: string;
};

export function drawScene(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  options: CanvasOptions = {},
): void {
  ctx.clearRect(0, 0, scene.width, scene.height);
  if (options.opaqueBackground) {
    ctx.fillStyle = options.opaqueBackground;
    ctx.fillRect(0, 0, scene.width, scene.height);
  }
  for (const node of scene.nodes) drawNode(ctx, node);
}

/** Создаёт canvas нужного размера и рисует на нём сцену. */
export function renderSceneToCanvas(scene: Scene, options: CanvasOptions = {}): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(scene.width));
  canvas.height = Math.max(1, Math.round(scene.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Браузер не выдал 2D-контекст canvas — растровый экспорт недоступен.');
  drawScene(ctx, scene, options);
  return canvas;
}
